import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Menu, X, Ticket, User, LogOut, ClipboardList, ScanLine, UserCircle, Calendar } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const GlobalNavbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll while mobile menu is open so the inner menu owns the scroll
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Close the menu on every route change (otherwise it stays open behind the new page on mobile)
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Authenticated promoters/admins see a live Guest List link that routes to /promoter
  const hasPromoterAccess = !!(user?.is_admin || user?.is_promoter);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/events', label: 'Events' },
    { path: '/discovery', label: 'Discovery' },
    { path: '/venues', label: 'Venues' },
    { path: '/team', label: 'Meet the Team' },
    { path: hasPromoterAccess ? '/promoter' : '/guestlist', label: 'Guest List' },
    { path: '/updates', label: 'Updates' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
    <nav 
      className={`fixed top-0 left-0 right-0 w-full z-[9990] transition-all duration-300 ${
        scrolled ? 'bg-[#050505]/95 backdrop-blur-md border-b border-zinc-800/50' : 'bg-[#050505]/70 backdrop-blur-sm'
      }`}
      data-testid="global-navbar"
    >
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3"
            data-testid="navbar-logo"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_venue-pulse-14/artifacts/8f7het1z_Unight%202d%20Logos%20%28TM%29-05.png"
              alt="Unight"
              className="h-8 md:h-10"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link 
                key={link.path}
                to={link.path} 
                className={`relative px-4 py-2 text-sm font-medium uppercase tracking-wider transition-colors ${
                  isActive(link.path) ? 'text-[#B5FF00]' : 'text-zinc-400 hover:text-white'
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
                <span 
                  className={`absolute bottom-0 left-4 right-4 h-0.5 bg-[#B5FF00] transition-transform duration-300 origin-left ${
                    isActive(link.path) ? 'scale-x-100' : 'scale-x-0'
                  }`}
                />
              </Link>
            ))}
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className="ml-4 flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-zinc-400 hover:text-[#B5FF00] transition-colors"
                    data-testid="user-menu-trigger"
                  >
                    <User size={18} strokeWidth={1.5} />
                    <span className="hidden lg:inline">{user.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="end" 
                  className="w-48 bg-[#0A0A0A] border-zinc-800 text-white"
                >
                  <DropdownMenuItem asChild>
                    <Link 
                      to="/profile" 
                      className="flex items-center gap-2 cursor-pointer"
                      data-testid="nav-profile"
                    >
                      <UserCircle size={16} strokeWidth={1.5} />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link 
                      to="/my-tickets" 
                      className="flex items-center gap-2 cursor-pointer"
                      data-testid="nav-my-tickets"
                    >
                      <Ticket size={16} strokeWidth={1.5} />
                      My Tickets
                    </Link>
                  </DropdownMenuItem>
                  {user.is_admin && (
                    <DropdownMenuItem asChild>
                      <Link 
                        to="/admin" 
                        className="flex items-center gap-2 cursor-pointer"
                        data-testid="nav-admin"
                      >
                        <User size={16} strokeWidth={1.5} />
                        Admin Panel
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {(user.is_admin || user.is_organizer) && (
                    <DropdownMenuItem asChild>
                      <Link
                        to="/organizer"
                        className="flex items-center gap-2 cursor-pointer"
                        data-testid="nav-organizer"
                      >
                        <Calendar size={16} strokeWidth={1.5} />
                        Organizer Panel
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {(user.is_admin || user.is_promoter || user.is_organizer) && (
                    <DropdownMenuItem asChild>
                      <Link 
                        to="/promoter" 
                        className="flex items-center gap-2 cursor-pointer"
                        data-testid="nav-promoter"
                      >
                        <ClipboardList size={16} strokeWidth={1.5} />
                        Promoter Panel
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {(user.is_admin || user.is_promoter || user.is_organizer) && (
                    <DropdownMenuItem asChild>
                      <Link 
                        to="/scan" 
                        className="flex items-center gap-2 cursor-pointer"
                        data-testid="nav-scan"
                      >
                        <ScanLine size={16} strokeWidth={1.5} />
                        Scan Tickets
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-zinc-800" />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="flex items-center gap-2 cursor-pointer text-red-400 focus:text-red-400"
                    data-testid="nav-logout"
                  >
                    <LogOut size={16} strokeWidth={1.5} />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link 
                to="/login" 
                className="ml-4 btn-primary text-xs py-2 px-4"
                data-testid="nav-login"
              >
                Sign In
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="flex md:hidden text-zinc-400 hover:text-white transition-colors p-2"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
          </button>
        </div>
      </div>
    </nav>

    {/* Mobile menu — rendered as a SIBLING of <nav> (not a child) so its
        `fixed inset-0` positioning is viewport-relative. Any Tailwind
        `container` wrapper on the nav can create layout constraints that
        collapse a fixed child's height to 0, which is exactly what happened
        after the recent redesign — "menu opens, no items appear". */}
    <div
      className={`md:hidden fixed inset-x-0 top-16 bottom-0 z-[9989] bg-[#050505]/98 backdrop-blur-lg transition-all duration-300 overflow-y-auto overscroll-contain ${
        isOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
      }`}
      style={{ WebkitOverflowScrolling: 'touch' }}
      data-testid="mobile-menu-overlay"
    >
          <div className="container mx-auto px-4 py-6 pb-32">
            <div className="flex flex-col gap-1">
              {navLinks.map((link, index) => (
                <Link 
                  key={link.path}
                  to={link.path} 
                  className={`py-3 text-xl font-['Josefin_Sans'] uppercase tracking-wider border-b border-zinc-800 transition-all duration-300 ${
                    isActive(link.path) ? 'text-[#B5FF00]' : 'text-white'
                  }`}
                  style={{ 
                    animationDelay: `${index * 50}ms`,
                    animation: isOpen ? 'slideInLeft 0.3s ease-out forwards' : 'none'
                  }}
                  onClick={() => setIsOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              
              {user ? (
                <>
                  <Link 
                    to="/profile" 
                    className="py-3 text-xl font-['Josefin_Sans'] uppercase tracking-wider text-white border-b border-zinc-800"
                    onClick={() => setIsOpen(false)}
                    data-testid="mobile-nav-profile"
                  >
                    Profile
                  </Link>
                  <Link 
                    to="/my-tickets" 
                    className="py-3 text-xl font-['Josefin_Sans'] uppercase tracking-wider text-white border-b border-zinc-800"
                    onClick={() => setIsOpen(false)}
                  >
                    My Tickets
                  </Link>
                  {user.is_admin && (
                    <Link 
                      to="/admin" 
                      className="py-3 text-xl font-['Josefin_Sans'] uppercase tracking-wider text-white border-b border-zinc-800"
                      onClick={() => setIsOpen(false)}
                    >
                      Admin Panel
                    </Link>
                  )}
                  {(user.is_admin || user.is_organizer) && (
                    <Link
                      to="/organizer"
                      className="py-3 text-xl font-['Josefin_Sans'] uppercase tracking-wider text-white border-b border-zinc-800"
                      onClick={() => setIsOpen(false)}
                      data-testid="mobile-nav-organizer"
                    >
                      Organizer Panel
                    </Link>
                  )}
                  {(user.is_admin || user.is_promoter || user.is_organizer) && (
                    <Link 
                      to="/promoter" 
                      className="py-3 text-xl font-['Josefin_Sans'] uppercase tracking-wider text-white border-b border-zinc-800"
                      onClick={() => setIsOpen(false)}
                    >
                      Promoter Panel
                    </Link>
                  )}
                  {(user.is_admin || user.is_promoter || user.is_organizer) && (
                    <Link 
                      to="/scan" 
                      className="py-3 text-xl font-['Josefin_Sans'] uppercase tracking-wider text-white border-b border-zinc-800 flex items-center gap-2"
                      onClick={() => setIsOpen(false)}
                      data-testid="mobile-nav-scan"
                    >
                      Scan Tickets
                    </Link>
                  )}
                  <button 
                    onClick={() => { handleLogout(); setIsOpen(false); }}
                    className="py-3 text-xl font-['Josefin_Sans'] uppercase tracking-wider text-red-400 text-left"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <Link 
                  to="/login" 
                  className="mt-4 btn-primary text-center"
                  onClick={() => setIsOpen(false)}
                >
                  Sign In
                </Link>
              )}
            </div>
          </div>
    </div>
    </>
  );
};

export default GlobalNavbar;
